import { logError } from '@/utils/logger';
import SearchableSelect from '@/components/SearchableSelect';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, CheckCircle, MagnifyingGlass, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const Assignments = () => {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [formData, setFormData] = useState({
    agent_id: '', client_id: '', site_name: 'none',
    start_date: '', shift: 'day',
  });
  const [agentSearch, setAgentSearch] = useState('');
  const [canEdit, setCanEdit] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [assignmentsRes, agentsRes, clientsRes, meRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assignments`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agents`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: 'include' }),
      ]);
      if (assignmentsRes.ok) setAssignments(await assignmentsRes.json());
      if (agentsRes.ok) setAgents(await agentsRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (meRes.ok) {
        const me = await meRes.json();
        setCanEdit((me.permissions?.can_edit || []).includes('assignments'));
      }
    } catch (error) {
      logError('Failed to fetch data:', error);
      toast.error('Erreur lors du chargement');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Only available agents can be assigned
  const availableAgents = useMemo(() => agents.filter(a => a.availability === 'available'), [agents]);

  // Filtered available agents by search
  const filteredAvailableAgents = useMemo(() => {
    if (!agentSearch) return availableAgents;
    const q = agentSearch.toLowerCase();
    return availableAgents.filter(a => a.name?.toLowerCase().includes(q) || a.matricule?.toLowerCase().includes(q) || a.phone?.includes(q));
  }, [availableAgents, agentSearch]);

  // Sites from selected client
  const selectedClientSites = useMemo(() => {
    const client = clients.find(c => c.client_id === formData.client_id);
    return client?.sites || [];
  }, [clients, formData.client_id]);

  // Filtered assignments
  const filteredAssignments = useMemo(() => {
    return assignments.filter(a => {
      const matchStatus = statusFilter === 'all' || a.status === statusFilter;
      if (!searchQuery) return matchStatus;
      const q = searchQuery.toLowerCase();
      const agentName = agents.find(ag => ag.agent_id === a.agent_id)?.name || a.agent_name || '';
      const clientName = clients.find(c => c.client_id === a.client_id)?.name || '';
      return matchStatus && (agentName.toLowerCase().includes(q) || clientName.toLowerCase().includes(q) || a.site_name?.toLowerCase().includes(q));
    });
  }, [assignments, agents, clients, searchQuery, statusFilter]);

  // Stats
  const activeCount = assignments.filter(a => a.status === 'active').length;
  const completedCount = assignments.filter(a => a.status === 'completed').length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agent_id || !formData.client_id) {
      toast.error('Sélectionnez un agent et un client');
      return;
    }
    try {
      const payload = {
        ...formData,
        site_name: formData.site_name === 'none' ? '' : formData.site_name,
      };
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assignments`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        toast.success('Affectation créée — agent marqué comme affecté');
        setIsDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      logError('Failed to create assignment:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleEndAssignment = async (assignmentId, agentName) => {
    if (!window.confirm(`Terminer l'affectation de ${agentName} ? L'agent redeviendra disponible.`)) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assignments/${assignmentId}/end`, {
        method: 'PUT', credentials: 'include',
      });
      if (response.ok) {
        toast.success(`Affectation terminée — ${agentName} est maintenant disponible`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch { toast.error('Erreur'); }
  };

  const resetForm = () => {
    setFormData({ agent_id: '', client_id: '', site_name: 'none', start_date: '', shift: 'day' });
    setAgentSearch('');
  };

  const getAgentName = (id, fallback) => agents.find(a => a.agent_id === id)?.name || fallback || id;
  const getClientName = (id) => clients.find(c => c.client_id === id)?.name || id;
  const getAgentPhoto = (id) => agents.find(a => a.agent_id === id)?.photo_url;
  const getShiftLabel = (shift) => ({ day: 'Jour', night: 'Nuit', rotating: 'Rotation' }[shift] || shift);
  const getShiftColor = (shift) => ({ day: 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30', night: 'bg-[#8B5CF6]/20 text-[#8B5CF6] border-[#8B5CF6]/30', rotating: 'bg-[#2563EB]/20 text-[#2563EB] border-[#2563EB]/30' }[shift] || '');

  if (loading) return <div className="text-[#94A3B8]">{t('common.loading')}</div>;

  return (
    <div className="space-y-6" data-testid="assignments-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight">{t('assignments.title')}</h1>
          <p className="text-[#94A3B8] mt-1 text-sm sm:text-base">Résultat dynamique des statuts agents</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            {canEdit ? (
              <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-assignment-button">
                <Plus size={20} className="mr-2" weight="bold" /> Nouvelle Affectation
              </Button>
            ) : <span />}
          </DialogTrigger>
          <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-2xl font-bold">Nouvelle Affectation</DialogTitle></DialogHeader>

            {availableAgents.length === 0 && (
              <div className="flex items-center gap-3 p-4 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-md">
                <Warning size={20} className="text-[#F59E0B] flex-shrink-0" />
                <p className="text-sm text-[#F59E0B]">Aucun agent disponible. Tous les agents sont déjà affectés ou indisponibles.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Agent search + selection */}
              <div>
                <Label className="text-[#F8FAFC]">Agent (disponibles uniquement)</Label>
                <div className="mt-1 space-y-2">
                  <div className="relative">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                    <Input
                      value={agentSearch}
                      onChange={(e) => setAgentSearch(e.target.value)}
                      placeholder="Rechercher un agent par nom..."
                      className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] pl-9"
                      data-testid="agent-search-assignment"
                    />
                  </div>
                  <div className="max-h-[160px] overflow-y-auto rounded-md border border-[#334155] bg-[#1E293B]/50">
                    {filteredAvailableAgents.length === 0 ? (
                      <p className="text-[#94A3B8] text-sm p-3 text-center">Aucun agent disponible</p>
                    ) : filteredAvailableAgents.map(a => (
                      <button
                        key={a.agent_id}
                        type="button"
                        onClick={() => setFormData({ ...formData, agent_id: a.agent_id })}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                          formData.agent_id === a.agent_id
                            ? 'bg-[#2563EB]/20 border-l-2 border-[#2563EB]'
                            : 'hover:bg-[#1E293B] border-l-2 border-transparent'
                        }`}
                        data-testid={`select-agent-${a.agent_id}`}
                      >
                        {a.photo_url ? (
                          <img src={a.photo_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-[#10B981] flex items-center justify-center text-white text-xs font-medium">{a.name?.charAt(0)}</div>
                        )}
                        <div className="min-w-0">
                          <p className="text-[#F8FAFC] text-sm font-medium truncate">{a.name}</p>
                          <p className="text-[#64748B] text-xs">{a.phone} {a.matricule ? `• ${a.matricule}` : ''}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  {formData.agent_id && (
                    <p className="text-xs text-[#10B981]">Sélectionné : {getAgentName(formData.agent_id)}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#F8FAFC]">Client</Label>
                  <div className="mt-1">
                    <SearchableSelect value={formData.client_id || ''} onValueChange={(v) => setFormData({ ...formData, client_id: v, site_name: 'none' })} options={clients.map(c => ({ value: c.client_id, label: c.name }))} placeholder="Sélectionner un client" searchPlaceholder="Rechercher un client..." testId="assignment-client-select" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#F8FAFC]">Site</Label>
                  <div className="mt-1">
                    <SearchableSelect value={formData.site_name} onValueChange={(v) => setFormData({ ...formData, site_name: v })} options={[{ value: 'none', label: 'Non précisé' }, ...selectedClientSites.map(s => ({ value: s, label: s }))]} placeholder="Choisir le site" searchPlaceholder="Rechercher un site..." testId="assignment-site-select" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#F8FAFC]">Poste</Label>
                  <Select value={formData.shift} onValueChange={(v) => setFormData({ ...formData, shift: v })}>
                    <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                      <SelectItem value="day" className="text-[#F8FAFC]">Jour</SelectItem>
                      <SelectItem value="night" className="text-[#F8FAFC]">Nuit</SelectItem>
                      <SelectItem value="rotating" className="text-[#F8FAFC]">Rotation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#F8FAFC]">Date de début</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="assignment-date" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
                <Button type="submit" disabled={availableAgents.length === 0} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">Affecter</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-4">
          <p className="text-[#94A3B8] text-xs uppercase mb-1">Total Agents</p>
          <p className="text-2xl font-bold text-[#F8FAFC]">{agents.length}</p>
        </div>
        <div className="bg-[#0F172A] border border-[#10B981]/30 rounded-md p-4">
          <p className="text-[#10B981] text-xs uppercase mb-1">Disponibles</p>
          <p className="text-2xl font-bold text-[#10B981]">{availableAgents.length}</p>
        </div>
        <div className="bg-[#0F172A] border border-[#2563EB]/30 rounded-md p-4">
          <p className="text-[#2563EB] text-xs uppercase mb-1">Affectations actives</p>
          <p className="text-2xl font-bold text-[#2563EB]">{activeCount}</p>
        </div>
        <div className="bg-[#0F172A] border border-[#94A3B8]/30 rounded-md p-4">
          <p className="text-[#94A3B8] text-xs uppercase mb-1">Terminées</p>
          <p className="text-2xl font-bold text-[#94A3B8]">{completedCount}</p>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Rechercher par agent, client ou site..." className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-[#0F172A] border-[#1E293B] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0F172A] border-[#1E293B]">
            <SelectItem value="all" className="text-[#F8FAFC]">Tous les statuts</SelectItem>
            <SelectItem value="active" className="text-[#F8FAFC]">Actives</SelectItem>
            <SelectItem value="completed" className="text-[#F8FAFC]">Terminées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Assignments Table */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-md overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1E293B]/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase">Agent</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase">Client</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase">Site</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase">Poste</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase">Début</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase">Statut</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-[#F8FAFC] uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1E293B]">
              {filteredAssignments.length === 0 ? (
                <tr><td colSpan="7" className="px-6 py-8 text-center text-[#94A3B8]">Aucune affectation trouvée</td></tr>
              ) : filteredAssignments.map((a) => (
                <tr key={a.assignment_id} className="hover:bg-[#1E293B]/30 transition-colors" data-testid={`assignment-row-${a.assignment_id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getAgentPhoto(a.agent_id) ? (
                        <img src={getAgentPhoto(a.agent_id)} alt="" className="h-8 w-8 rounded-full object-cover" />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-xs font-medium">{getAgentName(a.agent_id, a.agent_name).charAt(0)}</div>
                      )}
                      <span className="text-[#F8FAFC] font-medium">{getAgentName(a.agent_id, a.agent_name)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-[#94A3B8]">{getClientName(a.client_id)}</td>
                  <td className="px-6 py-4 text-[#94A3B8]">{a.site_name || '-'}</td>
                  <td className="px-6 py-4"><Badge className={`${getShiftColor(a.shift)} border`}>{getShiftLabel(a.shift)}</Badge></td>
                  <td className="px-6 py-4 text-[#94A3B8]">{new Date(a.start_date).toLocaleDateString('fr-FR')}</td>
                  <td className="px-6 py-4">
                    <Badge className={`border ${a.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' : 'bg-[#94A3B8]/20 text-[#94A3B8] border-[#94A3B8]/30'}`}>
                      {a.status === 'active' ? 'Actif' : 'Terminé'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {a.status === 'active' && canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => handleEndAssignment(a.assignment_id, getAgentName(a.agent_id, a.agent_name))} className="text-[#F59E0B] hover:text-white hover:bg-[#F59E0B]" title="Terminer l'affectation">
                        <CheckCircle size={18} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-[#1E293B]">
          {filteredAssignments.length === 0 ? (
            <div className="px-4 py-8 text-center text-[#94A3B8]">Aucune affectation trouvée</div>
          ) : filteredAssignments.map((a) => (
            <div key={a.assignment_id} className="p-4 space-y-3" data-testid={`assignment-card-${a.assignment_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  {getAgentPhoto(a.agent_id) ? (
                    <img src={getAgentPhoto(a.agent_id)} alt="" className="h-10 w-10 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-sm font-medium flex-shrink-0">{getAgentName(a.agent_id, a.agent_name).charAt(0)}</div>
                  )}
                  <div className="min-w-0">
                    <p className="text-[#F8FAFC] font-medium truncate">{getAgentName(a.agent_id, a.agent_name)}</p>
                    <p className="text-xs text-[#94A3B8] truncate">{getClientName(a.client_id)}</p>
                  </div>
                </div>
                {a.status === 'active' && canEdit && (
                  <Button size="sm" variant="ghost" onClick={() => handleEndAssignment(a.assignment_id, getAgentName(a.agent_id, a.agent_name))} className="text-[#F59E0B] hover:text-white hover:bg-[#F59E0B] h-8 w-8 p-0 flex-shrink-0" title="Terminer l'affectation">
                    <CheckCircle size={17} />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><p className="text-[10px] uppercase tracking-wider text-[#64748B]">Site</p><p className="text-[#94A3B8] truncate">{a.site_name || '-'}</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-[#64748B]">Début</p><p className="text-[#94A3B8]">{new Date(a.start_date).toLocaleDateString('fr-FR')}</p></div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={`${getShiftColor(a.shift)} border text-xs`}>{getShiftLabel(a.shift)}</Badge>
                <Badge className={`border text-xs ${a.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' : 'bg-[#94A3B8]/20 text-[#94A3B8] border-[#94A3B8]/30'}`}>
                  {a.status === 'active' ? 'Actif' : 'Terminé'}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Assignments;
